import { z } from "zod";
import { optionalFolderIdSchema } from "./folders.ts";
import {
  classificationLevelSchema,
  documentOriginSchema,
  projectDocumentSchema,
  readerSchema,
} from "./knowledge.ts";

/**
 * Project documents over the API (specs/project-files): upload intents, confirmation, listing
 * and metadata changes. Object keys and presigned URLs are never stored; an upload URL is
 * returned once, by the intent that minted it.
 */

/** Media types ingestion supports (the extractor in `@esi/ingest` handles exactly these). */
export const SUPPORTED_DOCUMENT_MEDIA_TYPES = [
  "application/pdf",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "text/csv",
  "text/markdown",
  "text/x-markdown",
  "text/html",
  "text/plain",
  "image/png",
  "image/jpeg",
] as const;

/** Images (OCR, captions, image embeddings): smaller files only, whatever the project allows. */
export const IMAGE_MEDIA_TYPES = ["image/png", "image/jpeg"] as const;

/** The largest image upload, in MB (spec `project-files` "Supported formats"). */
export const MAX_IMAGE_UPLOAD_MB = 20;

/** Images beyond this many pixels are refused before decoding (no pixel bombs). */
export const MAX_IMAGE_PIXELS = 40_000_000;

export function isImageMediaType(mediaType: string): boolean {
  const essence = mediaType.split(";")[0]?.trim().toLowerCase() ?? "";
  return (IMAGE_MEDIA_TYPES as readonly string[]).includes(essence);
}

/** The media type's essence (`text/plain; charset=utf-8` → `text/plain`) if supported, else null. */
export function supportedMediaType(mediaType: string): string | null {
  const essence = mediaType.split(";")[0]?.trim().toLowerCase() ?? "";
  return (SUPPORTED_DOCUMENT_MEDIA_TYPES as readonly string[]).includes(essence) ? essence : null;
}

/**
 * Types a browser tab may show inline (openspec change `open-in-browser`, design §1): passive
 * formats only. HTML, SVG and every other type are only ever served as an attachment.
 */
export const INLINE_MEDIA_TYPES = ["application/pdf", "image/png", "image/jpeg"] as const;

/**
 * Types the in-app viewer shows: their text comes from the API, rendered by the app. SVG
 * (openspec change `svg-preview`) is drawn only as an image of a sanitised `data:` URL, never as
 * a document: it never opens in a tab.
 */
export const VIEWER_MEDIA_TYPES = [
  "text/plain",
  "text/markdown",
  "text/x-markdown",
  "image/svg+xml",
] as const;

/** The largest file the in-app viewer shows (2 MB); larger ones are refused `too_large_to_view`. */
export const VIEWER_MAX_BYTES = 2 * 1024 * 1024;

export type OpenMode = "tab" | "viewer";

/**
 * How a stored type opens: `tab` (inline in a new browser tab), `viewer` (in-app) or null
 * (Download only), on the essence like `supportedMediaType`. The client picks its action with
 * it; the server, which is the authority, refuses anything else with `422 not_openable`.
 */
export function openModeOf(mediaType: string): OpenMode | null {
  const essence = mediaType.split(";")[0]?.trim().toLowerCase() ?? "";
  if ((INLINE_MEDIA_TYPES as readonly string[]).includes(essence)) {
    return "tab";
  }
  return (VIEWER_MEDIA_TYPES as readonly string[]).includes(essence) ? "viewer" : null;
}

/** The viewer route's body: a text, Markdown or SVG file's content, decoded as UTF-8. */
export const viewerContentSchema = z.object({
  title: z.string(),
  mediaType: z.enum(VIEWER_MEDIA_TYPES),
  content: z.string(),
});
export type ViewerContent = z.infer<typeof viewerContentSchema>;

/**
 * The largest upload the ingestion worker can process: `knowledge.maxUploadMb` cannot be set
 * above it, and the worker refuses larger objects (`too_large`) before reading them.
 */
export const MAX_UPLOAD_MB = 200;

/** `knowledge.maxUploadMb` in bytes (1 MB = 1 MiB, as the worker counts). */
export const uploadLimitBytes = (mb: number) => mb * 1024 * 1024;

/** The upload bound of a media type in MB: the project's limit, and at most 20 MB for images. */
export function uploadLimitMbFor(mediaType: string, projectMaxMb: number): number {
  return isImageMediaType(mediaType) ? Math.min(projectMaxMb, MAX_IMAGE_UPLOAD_MB) : projectMaxMb;
}

/**
 * Failure reasons a document can show (`ProjectDocument.failureReason`). Open: clients render an
 * unknown reason as-is.
 * - `ocr_failed`: a scanned page or image could not be transcribed (refused or unreadable);
 * - `ocr_page_cap`: more pages than `knowledge.ocrMaxPages`;
 * - `ocr_budget_reached`: the tenant's monthly OCR page budget is used up;
 * - `spend_limit`: paused, not failed: the tenant's hard monthly spend limit is reached. The
 *   hourly rollup queues the document again once the budget allows (spec `tenant-plans`).
 */
export const DOCUMENT_FAILURE_REASONS = [
  "no_text_layer",
  "unsupported_format",
  "corrupt",
  "empty",
  "too_large",
  "missing_object",
  "model_rejected",
  "index_drift",
  "unavailable",
  "ocr_failed",
  "ocr_page_cap",
  "ocr_budget_reached",
  "spend_limit",
  // conversation-attachments: the stored object did not match the declared size or type.
  "upload_mismatch",
] as const;
export type DocumentFailureReason = (typeof DOCUMENT_FAILURE_REASONS)[number];

/** The reason of a document paused by the tenant's hard spend limit (see above). */
export const SPEND_LIMIT_REASON = "spend_limit" satisfies DocumentFailureReason;

export const uploadDocumentRequestSchema = z.object({
  /** The file name, shown as the document title. */
  title: z.string().trim().min(1).max(255),
  mediaType: z.string().min(1).max(255),
  sizeBytes: z.int().min(1),
  /** Defaults to the project audience. */
  readers: z.array(readerSchema).min(1).max(100).optional(),
  /** Defaults to the project's `knowledge.defaultClassification`. */
  classification: z.string().min(1).max(40).optional(),
  origin: documentOriginSchema.optional(),
  /** A Files folder of the project; the Files root when absent or null. */
  folderId: optionalFolderIdSchema,
});
export type UploadDocumentRequest = z.infer<typeof uploadDocumentRequestSchema>;

/** Where and how to send the file: a single PUT with exactly these headers. */
export const uploadTargetSchema = z.object({
  url: z.string(),
  method: z.literal("PUT"),
  /** Every one of them is signed: the PUT fails without them. */
  headers: z.record(z.string(), z.string()),
  expiresAt: z.iso.datetime(),
});
export type UploadTarget = z.infer<typeof uploadTargetSchema>;

export const uploadDocumentResponseSchema = z.object({
  document: projectDocumentSchema,
  upload: uploadTargetSchema,
});
export type UploadDocumentResponse = z.infer<typeof uploadDocumentResponseSchema>;

export const documentListSchema = z.object({
  documents: z.array(projectDocumentSchema),
  /**
   * Documents of the project the caller cannot read; only their number is shown. Null for a
   * `guest`, to whom even the count would reveal the project's size.
   */
  restricted: z.int().min(0).nullable(),
  /**
   * The levels of the tenant's classification scheme that the listed documents use, so every
   * lister (guests too, who read no settings) can tell which of them may be downloaded.
   */
  classifications: z.array(classificationLevelSchema),
});
export type DocumentList = z.infer<typeof documentListSchema>;

export const updateDocumentRequestSchema = z.object({
  readers: z.array(readerSchema).min(1).max(100).optional(),
  classification: z.string().min(1).max(40).optional(),
  origin: documentOriginSchema.optional(),
  /**
   * The document's `metadataVersion` the change was based on: a different current one refuses
   * it (`409 version_mismatch`), so a concurrent change to its access is never overwritten.
   */
  expectedVersion: z.int().min(1).optional(),
});
export type UpdateDocumentRequest = z.infer<typeof updateDocumentRequestSchema>;

/**
 * The STS session tag that scopes a tenant-data role's S3 and KMS access to one tenant
 * (`${aws:PrincipalTag/esi-tenant}`): set by the object store when it assumes the role
 * (packages/aws tenant-credentials) and required by the role's trust (infra/lib/tenant-data.ts).
 */
export const TENANT_SESSION_TAG = "esi-tenant";
